import { memo } from "react"
import { Globe, Search, Bot, BookOpen } from "lucide-react"

import { useTheme, getSystemTheme, Theme } from "@f2e/ui/components/Theme"
import { cn } from "@f2e/ui/lib/utils"

import githubLogo from "@/assets/github-mark.svg"
import githubWhiteLogo from "@/assets/github-mark-white.svg"
import notionLogo from "@/assets/notion-logo.svg"
import linearLightLogo from "@/assets/linear-light-logo.svg"
import linearDarkLogo from "@/assets/linear-dark-logo.svg"
import googleDriveLogo from "@/assets/google-drive.svg"
import jiraLogo from "@/assets/jira-logo.svg"
import confluenceLogo from "@/assets/confluence-logo.svg"
import bitbucketLogo from "@/assets/bitbucket-logo.svg"
import { cva } from "class-variance-authority"
import { ContextProvider } from "@/types"

const providerLogoSources: Record<string, (isDark: boolean) => string> = {
  [ContextProvider.Github]: (isDark: boolean) =>
    isDark ? githubWhiteLogo : githubLogo,
  [ContextProvider.Notion]: () => notionLogo,
  [ContextProvider.Linear]: (isDark: boolean) =>
    isDark ? linearLightLogo : linearDarkLogo,
  [ContextProvider.GoogleDrive]: () => googleDriveLogo,
  [ContextProvider.Jira]: () => jiraLogo,
  [ContextProvider.Confluence]: () => confluenceLogo,
  [ContextProvider.Bitbucket]: () => bitbucketLogo,
}

const providerLogoVariants = cva("w-4 h-4 inline mr-1")

export interface ProviderLogoProps {
  className?: string
  provider: string
}

const ProviderLogo = memo(({ className, provider }: ProviderLogoProps) => {
  const { getTheme } = useTheme()
  const isDark =
    getTheme() === Theme.Dark ||
    (getTheme() === Theme.System && getSystemTheme() === Theme.Dark)

  if (provider === ContextProvider.Website) {
    return <Globe className={cn(providerLogoVariants(), className)} />
  }
  if (provider === "knowledge_base") {
    return <BookOpen className={cn(providerLogoVariants(), className)} />
  }

  if (provider === ContextProvider.Agent) {
    return (
      <Bot
        className={cn(
          providerLogoVariants(),
          className,
          "text-blue-600 dark:text-blue-500"
        )}
      />
    )
  }

  const getLogoSrc = providerLogoSources[provider]

  if (!getLogoSrc)
    return <Search className={cn(providerLogoVariants(), className)} />
  return (
    <img
      src={getLogoSrc(isDark)}
      alt={`${provider} Logo`}
      className={cn(providerLogoVariants(), className)}
    />
  )
})

export { ProviderLogo }
export default ProviderLogo
